import { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";

/**
 * Lightbox
 * --------
 * Fullscreen image viewer with pinch/scroll zoom + drag-to-pan + arrow keys.
 *
 *   props.images  – string[]
 *   props.index   – active image index
 *   props.onClose – () => void
 *   props.onIndex – (i) => void (called on prev/next)
 */
export default function Lightbox({ images, index, onClose, onIndex }) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });

  // Reset zoom/pan whenever the active image changes
  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [index]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
      else if (e.key === "ArrowRight") onIndex(Math.min(images.length - 1, index + 1));
      else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(5, s + 0.4));
      else if (e.key === "-" || e.key === "_") setScale((s) => Math.max(1, s - 0.4));
      else if (e.key === "0") { setScale(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [images.length, index, onClose, onIndex]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    setScale((s) => Math.min(5, Math.max(1, s + delta)));
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return; // no pan when not zoomed
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    setPan({
      x: dragStart.current.px + (e.clientX - dragStart.current.x),
      y: dragStart.current.py + (e.clientY - dragStart.current.y),
    });
  };
  const onPointerUp = () => setDragging(false);

  const src = images[index];
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/92 backdrop-blur-md flex flex-col"
      data-testid="lightbox"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white/90">
        <div className="font-mono text-[10px] uppercase tracking-widest">
          {index + 1} / {images.length} · Zoom {Math.round(scale * 100)}%
        </div>
        <div className="flex items-center gap-1.5">
          <IconBtn
            testid="lightbox-zoom-out"
            onClick={() => setScale((s) => Math.max(1, s - 0.4))}
            disabled={scale <= 1}
            title="Zoom out (−)"
          >
            <ZoomOut className="h-4 w-4" />
          </IconBtn>
          <IconBtn
            testid="lightbox-zoom-in"
            onClick={() => setScale((s) => Math.min(5, s + 0.4))}
            disabled={scale >= 5}
            title="Zoom in (+)"
          >
            <ZoomIn className="h-4 w-4" />
          </IconBtn>
          <IconBtn
            testid="lightbox-reset"
            onClick={() => { setScale(1); setPan({ x: 0, y: 0 }); }}
            title="Reset (0)"
            disabled={scale === 1 && pan.x === 0 && pan.y === 0}
          >
            <RotateCcw className="h-4 w-4" />
          </IconBtn>
          <IconBtn testid="lightbox-close" onClick={onClose} title="Close (Esc)">
            <X className="h-5 w-5" />
          </IconBtn>
        </div>
      </div>

      {/* Main viewport */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden select-none"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in" }}
        onClick={() => {
          if (scale === 1) setScale(2); // one-click zoom in
        }}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="max-w-[92vw] max-h-[80vh] object-contain pointer-events-none transition-transform duration-150 ease-out"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: "center center",
            willChange: "transform",
          }}
          data-testid="lightbox-image"
        />
        {hasPrev && (
          <NavBtn side="left" testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); onIndex(index - 1); }}>
            <ChevronLeft className="h-6 w-6" />
          </NavBtn>
        )}
        {hasNext && (
          <NavBtn side="right" testid="lightbox-next" onClick={(e) => { e.stopPropagation(); onIndex(index + 1); }}>
            <ChevronRight className="h-6 w-6" />
          </NavBtn>
        )}
      </div>

      {/* Thumbnail strip */}
      <div className="flex justify-center gap-2 px-4 py-3 overflow-x-auto">
        {images.map((u, i) => (
          <button
            key={u}
            onClick={(e) => { e.stopPropagation(); onIndex(i); }}
            data-testid={`lightbox-thumb-${i}`}
            className={`h-14 w-14 shrink-0 rounded overflow-hidden border-2 transition-colors ${
              i === index ? "border-primary" : "border-white/20 hover:border-white/60"
            }`}
            style={{ background: "#f4f6fa" }}
            aria-label={`Show angle ${i + 1}`}
          >
            <img src={u} alt="" className="w-full h-full object-contain" />
          </button>
        ))}
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, testid, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className="rounded-full p-2 bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

function NavBtn({ children, onClick, side, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`absolute top-1/2 -translate-y-1/2 ${
        side === "left" ? "left-3" : "right-3"
      } rounded-full p-3 bg-white/10 hover:bg-white/20 text-white transition-colors`}
      aria-label={side === "left" ? "Previous image" : "Next image"}
    >
      {children}
    </button>
  );
}
